'use client';

import { useEffect, useRef, useState, useCallback } from 'react';

import { getBinancePair } from '@/helpers/ChartSettings';

import type { I_TickerPrice } from '@/server/actions/market/getTickerPrice.action';
import type { T_Symbol } from '@/ts/types/T_Chart';

/**
 * Converts a symbol to Binance WebSocket stream format (lowercase with @ticker)
 */
function getBinanceStreamName(symbol: string): string {
  const pair = getBinancePair(symbol);
  return `${pair.toLowerCase()}@ticker`;
}

interface I_UseTickerWebSocketOptions {
  symbol: T_Symbol | string;
  onPriceUpdate?: (data: I_TickerPrice) => void;
  enabled?: boolean;
}

interface I_UseTickerWebSocketReturn {
  tickerPrice: I_TickerPrice | null;
  isConnected: boolean;
  error: string | null;
  reconnect: () => void;
}

export function useTickerWebSocket({
  symbol,
  onPriceUpdate,
  enabled = true
}: I_UseTickerWebSocketOptions): I_UseTickerWebSocketReturn {
  const [tickerPrice, setTickerPrice] = useState<I_TickerPrice | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const manuallyClosed = useRef(false);
  const currentSymbolRef = useRef<string>(symbol);

  const connect = useCallback(() => {
    if (!enabled) return;

    // Update current symbol ref
    currentSymbolRef.current = symbol;

    // Don't create new connection if one already exists for this symbol
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      return;
    }

    manuallyClosed.current = false;

    const streamName = getBinanceStreamName(symbol);
    const wsUrl = `wss://stream.binance.com:9443/ws/${streamName}`;

    try {
      console.log('Connecting to:', streamName);
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        setError(null);
        reconnectAttempts.current = 0;
      };

      ws.onmessage = event => {
        try {
          const data = JSON.parse(event.data);
          if (data.e === '24hrTicker') {
            const tickerData: I_TickerPrice = {
              symbol: data.s,
              price: data.c,
              priceChange: data.p,
              priceChangePercent: data.P,
              time: data.E
            };
            setTickerPrice(tickerData);
            onPriceUpdate?.(tickerData);
          }
        } catch {
          setError('Failed to parse price data');
        }
      };

      ws.onclose = _event => {
        // Ignore if this is no longer the current connection
        if (wsRef.current !== ws) return;

        setIsConnected(false);
        wsRef.current = null;

        // Ignore expected close events
        if (manuallyClosed.current) return;

        // Retry logic only for unexpected disconnects
        if (reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * 2 ** reconnectAttempts.current, 30000);
          reconnectTimeoutRef.current = setTimeout(() => {
            // Only reconnect if we're still on the same symbol
            if (currentSymbolRef.current === symbol) {
              reconnectAttempts.current++;
              connect();
            }
          }, delay);
        } else {
          setError('Failed to reconnect after multiple attempts');
        }
      };

      ws.onerror = event => {
        // Binance sometimes sends a "ping after close" event
        if (!manuallyClosed.current) {
          console.error(`WebSocket error for ${symbol}:`, event);
          setError('WebSocket connection error');
        }
      };
    } catch {
      setError('Failed to create WebSocket connection');
    }
  }, [symbol, enabled, onPriceUpdate]);

  const disconnect = useCallback(() => {
    manuallyClosed.current = true;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      const ws = wsRef.current;
      wsRef.current = null; // Clear ref first to prevent reconnect attempts
      try {
        if (
          ws.readyState === WebSocket.OPEN ||
          ws.readyState === WebSocket.CONNECTING
        ) {
          ws.close(1000, 'Symbol changed');
        }
      } catch {}
      // Remove all event listeners to prevent memory leaks
      ws.onopen = null;
      ws.onmessage = null;
      ws.onclose = null;
      ws.onerror = null;
    }
    setIsConnected(false);
  }, []);

  const reconnect = useCallback(() => {
    disconnect();
    reconnectAttempts.current = 0;
    setError(null);
    connect();
  }, [disconnect, connect]);

  useEffect(() => {
    if (!enabled) {
      disconnect();
      return;
    }

    // Reset reconnect attempts for new symbol
    reconnectAttempts.current = 0;
    setIsConnected(false);

    // Close any existing connection first (cleanup runs first)
    // Then connect to new symbol immediately
    connect();

    // Cleanup function to close connection on unmount or symbol change
    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, enabled]);

  return { tickerPrice, isConnected, error, reconnect };
}
